import React, { useState } from "react";
import { AppContainer } from "./Containers";
import { useNavigate } from "react-router";
import {
    Navbar,
    Button,
    ButtonVariant,
    Alignment,
    Drawer,
    Classes
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Manual } from "./Manual";
import { useLanguage } from "../context/LanguageContext";
import germanFlag from '../assets/de-flag.svg';
import britishFlag from '../assets/gb-flag.svg';

const sectionStyle: React.CSSProperties = {
    textAlign: "justify"
};

export const Impressum = () => {
    const navigate = useNavigate();
    const [isManualOpen, setIsManualOpen] = useState(false);
    const { t, language, setLanguage } = useLanguage();

    const contactIntroRaw = t('pages', 'impressum', 'sections', 'contact', 'intro') as string;

    function renderContactIntro(text: string) {
        const [beforeProject, afterProject] = text.split("{projectLink}");
        const [beforeInstitute, afterInstitute] = afterProject.split("{instituteLink}");

        return (
            <>
                {beforeProject}
                <a href="https://www.ph-karlsruhe.de/projekte/aqua-d" target="_blank" rel="noopener noreferrer">
                    {language === "de"
                        ? "„Aufgabenqualität im digital gestützten Unterricht“ (AQUA-d)"
                        : '"Task Quality in Digitally Supported Teaching" (AQUA-d)'}
                </a>
                {beforeInstitute}
                <a href="https://www.ph-karlsruhe.de/hochschule/fakultaet-fuer-natur-und-sozialwissenschaften/institut-fuer-oekonomie-und-ihre-didaktik" target="_blank" rel="noopener noreferrer">
                    {language === "de"
                        ? "Institut für Ökonomie und ihre Didaktik"
                        : "Institute for Economics and Economics Education"}
                </a>
                {afterInstitute}
            </>
        );
    }

    const people = t('pages', 'impressum', 'sections', 'contact', 'people', 'items', { returnObjects: true }) as Array<{ name: string; position: string }>;

    return (
        <AppContainer>
            {/* Blueprint Navbar - matches the main app style */}
            <Navbar style={{ marginBottom: "20px" }}>
                <Navbar.Group align={Alignment.START}>
                    <Navbar.Heading>
                        <Button
                            variant={ButtonVariant.MINIMAL}
                            icon={IconNames.ARROW_LEFT}
                            text={t('pages', 'impressum', 'backToApp')}
                            onClick={() => navigate("/app")}
                        />
                    </Navbar.Heading>
                    <Navbar.Divider />
                    <Button
                        icon={IconNames.HELP}
                        variant={ButtonVariant.MINIMAL}
                        text={t('navbar', 'manual')}
                        onClick={() => setIsManualOpen(true)}
                    />
                </Navbar.Group>
                <Navbar.Group align={Alignment.END}>
                    <Button
                        variant={ButtonVariant.MINIMAL}
                        icon={
                            <img
                                src={language === 'de' ? germanFlag : britishFlag}
                                alt={t('ui', 'changeLanguage')}
                                style={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: 2,
                                    verticalAlign: 'middle'
                                }}
                            />
                        }
                        text=""
                        onClick={() => setLanguage(language === 'de' ? 'en' : 'de')}
                        title={t('ui', 'changeLanguage')}
                        style={{ marginRight: 10, padding: '5px 10px' }}
                    />
                </Navbar.Group>
            </Navbar>

            {/* Anleitung Drawer */}
            <Drawer isOpen={isManualOpen} onClose={() => setIsManualOpen(false)}>
                <div className={Classes.DRAWER_BODY}>
                    <div className={Classes.DIALOG_BODY}>
                        <Button
                            icon={IconNames.ARROW_LEFT}
                            onClick={() => setIsManualOpen(false)}
                            text={t('navbar', 'closeManual')}
                            style={{ marginBottom: 24 }}
                        />
                        <Manual />
                    </div>
                </div>
            </Drawer>

            <h1>{t('pages', 'impressum', 'title')}</h1>
            {/* Sections below */}
            <section style={sectionStyle}>
                <h2>{t('pages', 'impressum', 'sections', 'howItWorks', 'title')}</h2>
                <p>{t('pages', 'impressum', 'sections', 'howItWorks', 'content')}</p>
            </section>
            <section style={sectionStyle}>
                <h2>{t('pages', 'impressum', 'sections', 'importance', 'title')}</h2>
                <p>{t('pages', 'impressum', 'sections', 'importance', 'content')}</p>
            </section>
            <section style={sectionStyle}>
                <h2>{t('pages', 'impressum', 'sections', 'origin', 'title')}</h2>
                <p>{t('pages', 'impressum', 'sections', 'origin', 'content')}</p>
            </section>
            <section style={sectionStyle}>
                <h2>{t('pages', 'impressum', 'sections', 'contact', 'title')}</h2>
                <p>
                    {renderContactIntro(contactIntroRaw)}
                </p>
                <ul>
                    {people.map((person, idx) => (
                        <li key={idx}>
                            <strong>{person.name}</strong><br />
                            {person.position}
                        </li>
                    ))}
                </ul>
            </section>
            <section style={sectionStyle}>
                <h2>{t('pages', 'impressum', 'sections', 'contactDetails', 'title')}</h2>
                <p>
                    <span role="img" aria-label="Map Marker" style={{ marginRight: 4 }}>📍</span>
                    <strong>{t('pages', 'impressum', 'sections', 'contactDetails', 'institution')}</strong><br />
                    {t('pages', 'impressum', 'sections', 'contactDetails', 'department')}<br />
                    {t('pages', 'impressum', 'sections', 'contactDetails', 'street')}<br />
                    {t('pages', 'impressum', 'sections', 'contactDetails', 'city')}<br />
                    {t('pages', 'impressum', 'sections', 'contactDetails', 'country')}<br />
                    <span role="img" aria-label="Envelope" style={{ marginRight: 4 }}>✉️</span>
                    {t('pages', 'impressum', 'sections', 'contactDetails', 'email')}: <a href="mailto:julian.polly@ph-karlsruhe.de">julian.polly@ph-karlsruhe.de</a><br />
                </p>
            </section>
            <section style={sectionStyle}>
                <p>{t('pages', 'impressum', 'sections', 'funding', 'content')}</p>
            </section>
        </AppContainer>
    );
};